import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Descriptions,
  Avatar,
  Switch,
  Tooltip,
  Popconfirm
} from 'antd';
import {
  UserOutlined,
  SoundOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
  WechatOutlined,
  UserAddOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { Search } = Input;

const UserList = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  
  // 模态框状态
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // 表单和搜索状态
  const [editForm] = Form.useForm();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filters, setFilters] = useState({
    user_type: '',
    status: ''
  });

  // 获取用户列表
  const fetchUsers = async (page = 1, pageSize = 10, searchParams = {}) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...searchParams
      });
      
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v2/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.data.users);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: data.data.pagination.total
        });
      } else {
        message.error(data.message || '获取用户列表失败');
      }
    } catch (error) {
      console.error('获取用户列表失败:', error);
      message.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 页面加载时获取数据
  useEffect(() => {
    const searchParams = {
      ...filters,
      search: searchKeyword
    };
    fetchUsers(1, 10, searchParams);
  }, [filters, searchKeyword]);

  // 更新用户状态
  const handleStatusChange = async (userId, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v2/users/${userId}/status`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ status: status ? 'active' : 'inactive' })
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success(`用户已${status ? '启用' : '禁用'}`);
        // 刷新列表
        fetchUsers(pagination.current, pagination.pageSize, {
          ...filters,
          search: searchKeyword
        });
      } else {
        message.error(data.message || '状态更新失败');
      }
    } catch (error) {
      console.error('状态更新失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 更新用户信息
  const handleUpdateUser = async (values) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v2/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(values)
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success('用户信息更新成功');
        setEditModalVisible(false);
        editForm.resetFields();
        setSelectedUser(null);
        // 刷新列表
        fetchUsers(pagination.current, pagination.pageSize, {
          ...filters,
          search: searchKeyword
        });
      } else {
        message.error(data.message || '更新失败');
      }
    } catch (error) {
      console.error('更新失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 删除用户
  const handleDeleteUser = async (userId, username) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v2/users/${userId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        message.success(`用户 ${username} 已删除`);
        // 刷新列表
        fetchUsers(pagination.current, pagination.pageSize, {
          ...filters,
          search: searchKeyword
        });
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('网络错误，请稍后重试');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '用户',
      key: 'user',
      width: 200,
      render: (_, record) => (
        <Space>
          <Avatar 
            src={record.wechat_avatar} 
            icon={<UserOutlined />}
            size="default"
          />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name || record.username}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>
              @{record.username}
              {record.wechat_openid && (
                <Tooltip title="已绑定微信">
                  <WechatOutlined style={{ marginLeft: 4, color: '#07c160' }} />
                </Tooltip>
              )}
            </div>
          </div>
        </Space>
      )
    },
    {
      title: '用户类型',
      dataIndex: 'user_type',
      key: 'user_type',
      width: 120,
      render: (type) => {
        const typeConfig = {
          super_admin: { color: 'purple', text: '超级管理员', icon: <SoundOutlined /> },
          school_admin: { color: 'blue', text: '学校管理员', icon: <SoundOutlined /> },
          teacher: { color: 'green', text: '教师用户', icon: <UserOutlined /> },
          parent: { color: 'orange', text: '家长用户', icon: <UserOutlined /> }
        };
        const config = typeConfig[type] || { color: 'default', text: type, icon: <UserOutlined /> };
        return (
          <Tag icon={config.icon} color={config.color}>
            {config.text}
          </Tag>
        );
      }
    },
    {
      title: '绑定状态',
      dataIndex: 'binding_status',
      key: 'binding_status',
      width: 110,
      render: (status, record) => {
        if (record.source === 'teachers') {
          return <Tag color="orange">未绑定微信</Tag>;
        } else if (record.wechat_openid) {
          return <Tag color="green">已绑定微信</Tag>;
        } else {
          return <Tag color="default">普通账号</Tag>;
        }
      }
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 180,
      render: (email) => email || '-'
    },
    {
      title: '所属学校',
      key: 'school',
      width: 150,
      render: (_, record) => record.school_name || '未分配'
    },
    {
      title: '联系电话',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone) => phone || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => {
        // 来自teachers表的用户，不允许在这里修改状态
        if (record.source === 'teachers') {
          return (
            <Tag color={status === 'active' ? 'green' : 'red'}>
              {status === 'active' ? '正常' : '停用'}
            </Tag>
          );
        }
        return (
          <Switch
            checked={status === 'active'}
            onChange={(checked) => handleStatusChange(record.id, checked)}
            checkedChildren="启用"
            unCheckedChildren="禁用"
            size="small"
          />
        );
      }
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (time) => new Date(time).toLocaleDateString()
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, record) => {
        // 来自teachers表的用户，提示去教师管理页面
        if (record.source === 'teachers') {
          return (
            <Space size="small">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                  setSelectedUser(record);
                  setDetailModalVisible(true);
                }}
              >
                详情
              </Button>
              <Tooltip title="请到教师管理页面操作">
                <Button
                  type="link"
                  size="small"
                  onClick={() => window.location.href = '/teachers'}
                >
                  教师管理
                </Button>
              </Tooltip>
            </Space>
          );
        }
        return (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedUser(record);
              setDetailModalVisible(true);
            }}
          >
            详情
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedUser(record);
              editForm.setFieldsValue({
                name: record.name,
                email: record.email,
                phone: record.phone,
                subject: record.subject
              });
              setEditModalVisible(true);
            }}
          >
            编辑
          </Button>
          {/* 超级管理员可以删除用户（不能删除自己和其他超级管理员） */}
          {authUser?.role === 'super_admin' && 
           record.user_type !== 'super_admin' && 
           record.id !== authUser?.id && (
            <Popconfirm
              title="确定要删除此用户吗？"
              description={`删除后该用户将无法登录系统`}
              onConfirm={() => handleDeleteUser(record.id, record.username)}
              okText="确定删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
        );
      }
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>用户管理</span>
            <Space>
              <Button 
                type="primary"
                icon={<UserAddOutlined />} 
                onClick={() => navigate('/users/registrations')}
              >
                注册申请
              </Button>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={() => fetchUsers(pagination.current, pagination.pageSize, {
                  ...filters,
                  search: searchKeyword
                })}
              >
                刷新
              </Button>
            </Space>
          </div>
        }
      >
        {/* 搜索和过滤器 */}
        <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center' }}>
          <Search
            placeholder="搜索用户名、邮箱或姓名"
            style={{ width: 300 }}
            onSearch={setSearchKeyword}
            allowClear
          />
          <Select
            placeholder="用户类型"
            style={{ width: 150 }}
            value={filters.user_type}
            onChange={(value) => setFilters({ ...filters, user_type: value })}
            allowClear
          >
            <Option value="super_admin">超级管理员</Option>
            <Option value="school_admin">学校管理员</Option>
            <Option value="teacher">教师用户</Option>
            <Option value="parent">家长用户</Option>
          </Select>
          <Select
            placeholder="用户状态"
            style={{ width: 120 }}
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            allowClear
          >
            <Option value="active">启用</Option>
            <Option value="inactive">禁用</Option>
          </Select>
        </div>

        {/* 用户列表表格 */}
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, pageSize) => {
              fetchUsers(page, pageSize, {
                ...filters,
                search: searchKeyword
              });
            }
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 用户详情模态框 */}
      <Modal
        title="用户详情"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedUser(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {selectedUser && (
          <div>
            {/* 用户头像和基本信息 */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Avatar 
                src={selectedUser.wechat_avatar} 
                icon={<UserOutlined />}
                size={80}
                style={{ marginBottom: 16 }}
              />
              <h3>{selectedUser.name || selectedUser.username}</h3>
              <Tag 
                color={
                  selectedUser.user_type === 'super_admin' ? 'purple' :
                  selectedUser.user_type === 'school_admin' ? 'blue' :
                  selectedUser.user_type === 'teacher' ? 'green' : 'orange'
                }
              >
                {
                  selectedUser.user_type === 'super_admin' ? '超级管理员' :
                  selectedUser.user_type === 'school_admin' ? '学校管理员' :
                  selectedUser.user_type === 'teacher' ? '教师用户' : '家长用户'
                }
              </Tag>
            </div>

            {/* 详细信息 */}
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="用户名">{selectedUser.username}</Descriptions.Item>
              <Descriptions.Item label="姓名">{selectedUser.name || '未设置'}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{selectedUser.email}</Descriptions.Item>
              <Descriptions.Item label="联系电话">{selectedUser.phone || '未填写'}</Descriptions.Item>
              <Descriptions.Item label="所属学校" span={2}>
                {selectedUser.school_name || '未分配'}
              </Descriptions.Item>
              
              {selectedUser.subject && (
                <Descriptions.Item label="任教科目">{selectedUser.subject}</Descriptions.Item>
              )}
              
              <Descriptions.Item label="账户状态">
                <Tag color={selectedUser.status === 'active' ? 'green' : 'red'}>
                  {selectedUser.status === 'active' ? '启用' : '禁用'}
                </Tag>
              </Descriptions.Item>
              
              <Descriptions.Item label="创建时间" span={2}>
                {new Date(selectedUser.created_at).toLocaleString()}
              </Descriptions.Item>
              
              {selectedUser.wechat_openid && (
                <>
                  <Descriptions.Item label="微信绑定">
                    <Tag color="green">
                      <WechatOutlined /> 已绑定
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="微信昵称">
                    {selectedUser.wechat_nickname || '未获取'}
                  </Descriptions.Item>
                </>
              )}
              
              {selectedUser.approved_by && (
                <>
                  <Descriptions.Item label="审核人">{selectedUser.approved_by}</Descriptions.Item>
                  <Descriptions.Item label="审核时间">
                    {new Date(selectedUser.approved_at).toLocaleString()}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 编辑用户模态框 */}
      <Modal
        title="编辑用户信息"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
          setSelectedUser(null);
        }}
        footer={null}
        width={600}
      >
        {selectedUser && (
          <Form
            form={editForm}
            onFinish={handleUpdateUser}
            layout="vertical"
          >
            <Form.Item
              name="name"
              label="姓名"
              rules={[{ required: true, message: '请输入姓名' }]}
            >
              <Input placeholder="请输入用户姓名" />
            </Form.Item>

            <Form.Item
              name="email"
              label="邮箱地址"
              rules={[
                { required: true, message: '请输入邮箱地址' },
                { type: 'email', message: '请输入有效的邮箱地址' }
              ]}
            >
              <Input placeholder="请输入邮箱地址" />
            </Form.Item>

            <Form.Item
              name="phone"
              label="联系电话"
              rules={[
                { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' }
              ]}
            >
              <Input placeholder="请输入联系电话" />
            </Form.Item>

            {selectedUser.user_type === 'teacher' && (
              <Form.Item
                name="subject"
                label="任教科目"
              >
                <Input placeholder="请输入任教科目" />
              </Form.Item>
            )}

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  保存修改
                </Button>
                <Button onClick={() => setEditModalVisible(false)}>
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default UserList;
